/**
 * Audition a drone from a list: sample-based drones play their first sample's prepared loop as-is (root pitch,
 * no drone processing); synth/freeze drones play the drone itself at Sa. One preview at a time; toggling the same
 * id stops it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Drone } from '@tonist/shared';
import { saHz } from '@tonist/shared';
import { getEngine } from '../engine';
import type { VoiceHandle } from '../engine/types';
import { useSettings } from '../store/settingsStore';

export function usePreview(): { playingId: string | null; toggle: (drone: Drone) => void; stop: () => void } {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const voice = useRef<VoiceHandle | null>(null);
  const engine = getEngine();

  const stop = useCallback(() => {
    engine.stopPreview?.();
    if (voice.current) { voice.current.release(); voice.current = null; }
    setPlayingId(null);
  }, [engine]);

  const toggle = useCallback((drone: Drone) => {
    if (playingId === drone.id) { stop(); return; }
    stop();
    const first = drone.sources[0];
    setPlayingId(drone.id);
    void (async () => {
      try {
        await engine.start();
        if (first && first.kind === 'sample' && engine.previewSample) {
          await engine.previewSample(first.sampleId);
        } else {
          await engine.loadDrone(drone);
          const hz = saHz(useSettings.getState().tonicPc, 0);
          voice.current = engine.noteOn(drone.id, hz, { radius: 0.5, velocity: 0 });
        }
      } catch {
        setPlayingId(null);
      }
    })();
  }, [engine, playingId, stop]);

  useEffect(() => () => { engine.stopPreview?.(); voice.current?.release(); }, [engine]);
  return { playingId, toggle, stop };
}
